import React, { useMemo } from 'react';
import { Typography } from 'antd';
import type { TitleProps } from 'antd/es/typography/Title';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { useResolveTokenColor } from '../../utils/resolveTokenColor';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

const { Title } = Typography;

/**
 * AndTitle Props 接口
 */
export interface AndTitleProps extends TitleProps, BaseComponentProps, DataSetConfig {
  /** 文本内容 */
  content?: string;
  /** 字体颜色 */
  color?: string;
  /** 组件样式配置 */
  styles?: React.CSSProperties;
}

/**
 * AndTitle 组件
 * 封装 Ant Design Typography.Title，支持数据绑定
 */
export const AndTitle: React.FC<AndTitleProps> = ({
  id,
  sessionId,
  develop,
  content,
  children,
  dataSet,
  dataSetField,
  level = 1,
  color,
  styles,
  ...restProps
}) => {
  const getDataSet = useAndStore((state) => state.getDataSet);
  const getProps = useAndStore((state) => state.getProps);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 从 store 获取动态 props
  const storeProps = useMemo(() => {
    if (sessionId && id) {
      return getProps(sessionId, id);
    }
    return null;
  }, [sessionId, id, getProps]);

  // 从数据集获取值
  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) {
      return getDataSet(dataSetKey, dataSetField);
    }
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 计算最终显示的内容
  const displayContent = useMemo(() => {
    if (develop) return content;
    if (storeValue !== undefined && storeValue !== null && storeValue !== '') {
      return String(storeValue);
    }
    if (storeProps?.content !== undefined) {
      return storeProps.content;
    }
    return content;
  }, [develop, storeValue, storeProps?.content, content]);

  // 解析 Design Token 颜色
  const effectiveColor = storeProps?.color ?? color;
  const resolvedColor = useResolveTokenColor(effectiveColor);

  // 自定义样式
  const customStyle = useMemo(() => {
    const style: React.CSSProperties = {};
    if (resolvedColor) {
      style.color = resolvedColor;
    }
    return style;
  }, [resolvedColor]);

  // 合并 props
  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (key !== 'content' && storeProps[key] !== undefined && storeProps[key] !== null) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  return (
    <Title level={level} {...mergedProps} style={{ ...customStyle, ...styles, ...mergedProps.style }}>
      {displayContent || children}
    </Title>
  );
};

export default AndTitle;
